/**
 * useTickBuffer — client-side rolling tick ring buffer (proposal 18).
 *
 * Samples the live quote for `symbol` from useSubscribedSymbolsStore and keeps
 * a bounded window of {t, bid, ask} points. Client-side only — no server
 * backfill: the buffer starts empty and fills while the screen is open.
 *
 * Updates are coalesced: the store is sampled ~4×/sec, and React state only
 * changes when a new distinct tick arrived, so consumers do not rerender at
 * raw tick rate.
 */

import { useEffect, useRef, useState } from "react";
import { useSubscribedSymbolsStore } from "../../subscribed-symbols/stores/useSubscribedSymbolsStore";

export interface TickPoint {
  /** ms epoch when the sample was taken client-side */
  t: number;
  bid: number;
  ask: number;
}

const SAMPLE_MS = 250; // ≤ 4 state updates per second
const MAX_POINTS = 600;

export function useTickBuffer(
  symbol: string,
  windowSec: number = 60
): TickPoint[] {
  const [points, setPoints] = useState<TickPoint[]>([]);
  const bufferRef = useRef<TickPoint[]>([]);

  useEffect(() => {
    // Reset on symbol (or window) change.
    bufferRef.current = [];
    setPoints([]);
    if (!symbol) return;

    let lastBid = NaN;
    let lastAsk = NaN;

    const sample = () => {
      const entry = useSubscribedSymbolsStore
        .getState()
        .byCurrency.get(symbol);
      const quote = entry?.quote;
      if (!quote || !(quote.bid > 0) || !(quote.ask > 0)) return;
      if (quote.bid === lastBid && quote.ask === lastAsk) return;
      lastBid = quote.bid;
      lastAsk = quote.ask;

      const now = Date.now();
      const buf = bufferRef.current;
      buf.push({ t: now, bid: quote.bid, ask: quote.ask });

      // Trim by time window and hard cap.
      const minT = now - windowSec * 1000;
      let drop = 0;
      while (drop < buf.length && buf[drop]!.t < minT) drop++;
      if (buf.length - drop > MAX_POINTS) drop = buf.length - MAX_POINTS;
      if (drop > 0) buf.splice(0, drop);

      setPoints(buf.slice());
    };

    sample();
    const id = setInterval(sample, SAMPLE_MS);
    return () => clearInterval(id);
  }, [symbol, windowSec]);

  return points;
}
